/**
 * Cart Reward Progress System
 * Handles milestone tracking, free product add/remove, abuse prevention,
 * and progress bar UI updates for the cart drawer.
 *
 * SETUP: The variantId values below map each product milestone to the reward
 * added to the cart. The "Free Stand" reward relies on the Shopify automatic
 * discount "Free Ceramic Stand on ₹2499+" to zero it out at checkout — the
 * stand variant must belong to the "Ceramic Stands – Free Gift" collection the
 * discount targets. The "Free Gift" reward is a genuine ₹0 product.
 */

// ─── Configuration ────────────────────────────────────────────────────────────
const REWARDS_CONFIG = {
  // Thresholds are in paise (Shopify stores money × 100). ₹590 = 59000.
  milestones: [
    {
      threshold: 59000,
      label: 'Free Delivery',
      type: 'visual', // no product added — just a UI milestone
    },
    {
      threshold: 149900,
      label: 'Free Gift',
      type: 'product',
      variantId: 44028726345816, // "Free Gift" product (₹0)
      productTag: 'free-gift-reward',
    },
    {
      threshold: 249900,
      label: 'Free Stand',
      type: 'product',
      // "Havan Cup Stand – Teal Floral Ceramic Base" from the "Ceramic Stands – Free Gift"
      // collection; made free at checkout by the "Free Ceramic Stand on ₹2499+" auto discount.
      variantId: 44347154301016,
      productTag: 'free-stand-reward',
    },
  ],
  maxThreshold: 249900,
  freeRewardProperty: '_free_reward', // line item property key used to identify free items
};

// ─── State ────────────────────────────────────────────────────────────────────
let _isProcessing = false; // prevents concurrent reward validation calls
const _failedRewardVariants = new Set(); // variants whose add failed (e.g. out of stock) — don't retry this session

// ─── Utilities ────────────────────────────────────────────────────────────────

/**
 * Sum line prices for all cart items that are NOT free reward products.
 * @param {Array} items - cart.items array from /cart.js response
 * @returns {number} paid subtotal in paise
 */
function calculateCartTotalExcludingFreeItems(items) {
  return items.reduce((total, item) => {
    const isFree = item.properties && item.properties[REWARDS_CONFIG.freeRewardProperty] === 'true';
    return isFree ? total : total + item.line_price;
  }, 0);
}

/**
 * Check whether a free reward variant is already in the cart.
 * @param {Array} items
 * @param {number} variantId
 * @returns {object|null} the cart line item if found
 */
function findFreeItemInCart(items, variantId) {
  return items.find(
    (item) =>
      item.variant_id === variantId &&
      item.properties &&
      item.properties[REWARDS_CONFIG.freeRewardProperty] === 'true'
  ) || null;
}

/**
 * Add a free reward product to the cart (idempotent — skips if already present).
 * @param {number} variantId
 * @param {Array} currentItems - current cart items (to check idempotency)
 * @returns {Promise<object>} resolved cart state
 */
async function addFreeProduct(variantId, currentItems = []) {
  if (!variantId) {
    console.warn('[CartRewards] variantId is null — update REWARDS_CONFIG before going live.');
    return null;
  }

  if (_failedRewardVariants.has(variantId)) {
    return null; // previous add failed (e.g. sold out) — don't hammer the API every cart change
  }

  if (findFreeItemInCart(currentItems, variantId)) {
    return null; // already in cart, nothing to do
  }

  const properties = {};
  properties[REWARDS_CONFIG.freeRewardProperty] = 'true';

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      properties,
    }),
  });

  if (!response.ok) {
    const err = await response.json();
    console.error('[CartRewards] addFreeProduct failed:', err);
    _failedRewardVariants.add(variantId);
    return null;
  }

  return response.json();
}

/**
 * Remove a free reward product from the cart by variant ID.
 * Uses /cart/change.js to set quantity to 0 on the specific line.
 * @param {number} variantId
 * @param {Array} currentItems
 * @returns {Promise<object|null>}
 */
async function removeFreeProduct(variantId, currentItems = []) {
  if (!variantId) return null;

  const item = findFreeItemInCart(currentItems, variantId);
  if (!item) return null; // not in cart, nothing to remove

  // Use the line item key — unlike a numeric line index, it stays valid even
  // after other lines have been removed in the same validation pass.
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: item.key, quantity: 0 }),
  });

  if (!response.ok) {
    const err = await response.json();
    console.error('[CartRewards] removeFreeProduct failed:', err);
    return null;
  }

  return response.json();
}

/**
 * Fetch the latest cart state from Shopify.
 * @returns {Promise<object>} cart object
 */
async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });
  return response.json();
}

// ─── Core Logic ───────────────────────────────────────────────────────────────

/**
 * Verify every reward line is actually free at the server and remove any that
 * isn't. This is the guard against a broken/expired automatic discount silently
 * charging the customer for a "free" item. Uses `final_line_price` (post-discount),
 * NOT `line_price`, and NOT the client-set `_free_reward` property.
 * @returns {Promise<boolean>} whether any overcharged reward line was removed
 */
async function enforceFreeItemsAreFree() {
  try {
    const cart = await fetchCart();
    const items = cart.items || [];
    let removedAny = false;

    for (const item of items) {
      const isReward = item.properties && item.properties[REWARDS_CONFIG.freeRewardProperty] === 'true';
      if (isReward && item.final_line_price > 0) {
        console.warn(
          '[CartRewards] Reward line NOT zeroed by its discount (final_line_price=' +
            item.final_line_price + ' paise) — removing to avoid overcharge. variant=' + item.variant_id +
            '. Check the backing automatic discount is ACTIVE in Shopify admin.'
        );
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
        // Don't re-add this reward again this session — the discount is broken.
        _failedRewardVariants.add(item.variant_id);
        removedAny = true;
      }
    }
    return removedAny;
  } catch (err) {
    console.error('[CartRewards] enforceFreeItemsAreFree error:', err);
    return false;
  }
}

/**
 * Main orchestrator.
 * 1. Calculates paid subtotal (excluding free reward items).
 * 2. Blocks abuse: if paidTotal === 0 but free items exist → remove them all.
 * 3. Adds/removes product-type milestones based on threshold.
 * 4. Updates the progress bar UI.
 *
 * @param {object} cartData - cart object (from /cart.js or the sections response)
 */
async function validateCartRewards(cartData) {
  if (_isProcessing) return;
  _isProcessing = true;

  try {
    // Reuse the cart payload the caller already fetched (cart.js passes the
    // /cart/change response as event.cartData) to avoid a redundant network
    // round-trip on every quantity change. Fall back to a fetch only when we
    // weren't handed a usable cart (e.g. the drawer-open hook).
    const cart = cartData && Array.isArray(cartData.items) ? cartData : await fetchCart();
    const items = cart.items || [];
    const paidTotal = calculateCartTotalExcludingFreeItems(items);

    const productMilestones = REWARDS_CONFIG.milestones.filter((m) => m.type === 'product');
    let cartChanged = false;

    // Abuse prevention: cart has items but ALL of them are free → purge
    if (items.length > 0 && paidTotal === 0) {
      for (const milestone of productMilestones) {
        const result = await removeFreeProduct(milestone.variantId, items);
        if (result) cartChanged = true;
      }
      updateRewardBarUI(0);
      if (cartChanged) await refreshCartDrawer();
      return;
    }

    // Process milestones from highest to lowest to avoid conflicts
    const sortedMilestones = [...productMilestones].sort((a, b) => b.threshold - a.threshold);

    for (const milestone of sortedMilestones) {
      if (paidTotal >= milestone.threshold) {
        const result = await addFreeProduct(milestone.variantId, items);
        if (result) cartChanged = true;
      } else {
        const result = await removeFreeProduct(milestone.variantId, items);
        if (result) cartChanged = true;
      }
    }

    // SAFETY NET: a reward line is only truly "free" if Shopify actually zeroed
    // it via the backing automatic discount (Free Gift is a ₹0 product; Free
    // Stand relies on "Free Ceramic Stand on ₹2499+"). If that discount is
    // expired/missing/misconfigured, the line reaches checkout at full price and
    // the customer is charged for a "free" item. Re-read the authoritative cart
    // and remove any _free_reward line the server did NOT zero. Never trust the
    // client-set _free_reward property as proof of price.
    if (cartChanged) {
      const removedUnfree = await enforceFreeItemsAreFree();
      if (removedUnfree) cartChanged = true;
    }

    // Update UI with current paid total (before any cart refresh)
    updateRewardBarUI(paidTotal);

    // Verify automatic discounts for 2/4/5/6 items
    validateAutomaticDiscounts(cart);

    // If we added/removed products, re-render the drawer to reflect changes
    if (cartChanged) await refreshCartDrawer();

  } catch (err) {
    console.error('[CartRewards] validateCartRewards error:', err);
  } finally {
    _isProcessing = false;
  }
}

// ─── UI Updates ───────────────────────────────────────────────────────────────

/**
 * Update all DOM elements of the reward progress bar.
 * @param {number} paidTotal - paid subtotal in paise
 */
function updateRewardBarUI(paidTotal) {
  const bar = document.getElementById('cart-reward-bar');
  if (!bar) return;

  const fill = document.getElementById('cart-reward-fill');
  const message = document.getElementById('cart-reward-message');
  const amountMarkers = bar.querySelectorAll('.cart-reward__amount-marker');
  const milestoneEls = bar.querySelectorAll('.cart-reward__milestone');
  const { milestones, maxThreshold } = REWARDS_CONFIG;

  // Fill percentage capped at 100
  const pct = Math.min((paidTotal / maxThreshold) * 100, 100);
  if (fill) {
    const previousPct = Number(bar.dataset.fillPct || 0);
    const isIncreasing = pct > previousPct;

    // Keep a tiny visible fill when cart has value so progress is always noticeable.
    const visiblePct = paidTotal > 0 ? Math.max(pct, 2) : 0;

    if (isIncreasing) {
      fill.classList.add('is-increasing');
      window.clearTimeout(fill._increaseAnimTimeout);
      fill._increaseAnimTimeout = window.setTimeout(() => {
        fill.classList.remove('is-increasing');
      }, 850);
    }

    fill.style.width = `${visiblePct}%`;
  }

  // Milestone amount-marker + label states
  milestones.forEach((milestone, index) => {
    const achieved = paidTotal >= milestone.threshold;
    if (amountMarkers[index]) amountMarkers[index].classList.toggle('is-achieved', achieved);
    if (milestoneEls[index]) milestoneEls[index].classList.toggle('is-achieved', achieved);
  });

  // Dynamic message
  if (message) {
    if (paidTotal >= maxThreshold) {
      message.innerHTML = "You've unlocked all rewards! 🎉";
    } else {
      const next = milestones.find((m) => paidTotal < m.threshold);
      if (next) {
        const amountAway = next.threshold - paidTotal;
        // Convert paise to rupees for display
        const rupees = Math.ceil(amountAway / 100);
        message.innerHTML = `You are <strong>₹${rupees}</strong> away from <strong>${next.label}</strong>!`;
      }
    }
  }

  // Sync data attribute so Liquid-rendered value stays in sync
  bar.dataset.cartTotal = paidTotal;
  bar.dataset.fillPct = pct;
}

// ─── Cart Drawer Refresh ───────────────────────────────────────────────────────

/**
 * Re-fetch and re-render the cart drawer section after reward product changes.
 * This mirrors the existing Dawn onCartUpdate pattern in cart.js.
 */
async function refreshCartDrawer() {
  try {
    const root = window.Shopify?.routes?.root || '/';
    // Fetch the drawer AND the header cart-count bubble together. The bubble
    // MUST be refreshed here too: reward add/remove/purge changes the cart via
    // /cart/add|change (not the CartItems component), so nothing else updates
    // the badge — without this it shows a stale count (e.g. "1" over an empty
    // cart) after a free line is purged. Parallel so there's no added latency.
    const [drawerText, bubbleText] = await Promise.all([
      fetch(`${root}?section_id=cart-drawer`).then((r) => r.text()),
      fetch(`${root}?section_id=cart-icon-bubble`).then((r) => r.text())
    ]);
    const html = new DOMParser().parseFromString(drawerText, 'text/html');

    const itemsTarget = document.querySelector('#CartDrawer-CartItems');
    const itemsFresh = html.querySelector('#CartDrawer-CartItems');

    if (itemsTarget && itemsFresh) {
      // Morph the item rows in place (only the free-reward row is actually
      // added/removed) and swap just the totals + progress bar. Leaving the
      // upsell/recommendations untouched avoids replaying their animations,
      // and unchanged product rows keep their images without flashing.
      if (window.morphCartItemsContents) {
        window.morphCartItemsContents(itemsTarget, itemsFresh.innerHTML);
      } else {
        itemsTarget.innerHTML = itemsFresh.innerHTML;
      }
      for (const selector of ['#cart-reward-bar', '.cart-drawer__footer']) {
        const target = document.querySelector(selector);
        const source = html.querySelector(selector);
        if (target && source) target.replaceWith(source);
      }
    } else {
      // Structure changed (e.g. cart became empty after the abuse purge) —
      // swap the whole drawer and sync the empty-state class.
      const cartDrawerEl = document.getElementById('CartDrawer');
      const freshDrawer = html.querySelector('#CartDrawer');
      if (cartDrawerEl && freshDrawer) cartDrawerEl.innerHTML = freshDrawer.innerHTML;
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', !itemsFresh);
    }

    // Sync the header cart badge with the real item count.
    const bubbleTarget = document.getElementById('cart-icon-bubble');
    if (bubbleTarget && bubbleText) {
      const bubbleDoc = new DOMParser().parseFromString(bubbleText, 'text/html');
      const bubbleInner = bubbleDoc.querySelector('.shopify-section') || bubbleDoc.body;
      if (bubbleInner) bubbleTarget.innerHTML = bubbleInner.innerHTML;
    }
  } catch (err) {
    console.error('[CartRewards] refreshCartDrawer error:', err);
  }
}

// ─── Event Hooks ──────────────────────────────────────────────────────────────

/**
 * Subscribe to Dawn's pub/sub cart update event.
 * This fires after every quantity change, item remove, or add-to-cart.
 */
function initCartRewardListeners() {
  // Guard: ensure pub/sub is available (it's defined in global.js)
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
    // Retry once scripts have loaded
    document.addEventListener('DOMContentLoaded', initCartRewardListeners, { once: true });
    return;
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    // cartData may be on event.cartData (from cart-items updateQuantity publish)
    validateCartRewards(event?.cartData || {});
  });

  // Also run on cart drawer open (catches cases where rewards need syncing on open)
  const cartDrawerEl = document.querySelector('cart-drawer');
  if (cartDrawerEl) {
    const originalOpen = cartDrawerEl.open?.bind(cartDrawerEl);
    if (typeof originalOpen === 'function') {
      cartDrawerEl.open = function (...args) {
        originalOpen(...args);
        validateCartRewards({});
      };
    }
  }
}

// ─── Init ─────────────────────────────────────────────────────────────────────

// Run as soon as the script loads (deferred, so DOM is ready)
initCartRewardListeners();

/**
 * Validates that the server-applied discounts match the builder calculation.
 * Sets data-discount-valid="true" or "false" on the cart-drawer element.
 */
function validateAutomaticDiscounts(cart) {
  const eligibleHandles = [
    'your-morning-clarity',
    'your-midday-renewal',
    'your-night-unwind',
    'kashi-havan-cups',
    'moksae-intention-havan-cups'
  ];
  let eligibleCount = 0;
  let eligibleSubtotal = 0;
  
  const items = cart.items || [];
  items.forEach(item => {
    if (eligibleHandles.includes(item.handle)) {
      eligibleCount += item.quantity;
      eligibleSubtotal += item.original_line_price;
    }
  });

  // Calculate expected discount
  let expectedPct = 0;
  if (eligibleCount >= 6) expectedPct = 20;
  else if (eligibleCount >= 5) expectedPct = 15;
  else if (eligibleCount >= 4) expectedPct = 10;
  else if (eligibleCount >= 2) expectedPct = 5;

  const expectedDiscountAmount = Math.round(eligibleSubtotal * (expectedPct / 100));

  let actualDiscountAmount = 0;
  if (cart.cart_level_discount_applications) {
    cart.cart_level_discount_applications.forEach(disc => {
      actualDiscountAmount += disc.total_allocated_amount;
    });
  }

  items.forEach(item => {
    if (eligibleHandles.includes(item.handle)) {
      if (item.line_level_discount_allocations) {
        item.line_level_discount_allocations.forEach(alloc => {
          actualDiscountAmount += alloc.amount;
        });
      }
    }
  });

  // Only flag invalid when the server applied LESS than the builder promised
  // (broken promise). Other automatic discounts (e.g. "Buy 1 Get 1 15% Off",
  // the free-stand reward) legitimately push the actual total above the
  // expected tier amount — that must not strip discount styling from the UI.
  const isMatch = actualDiscountAmount + 5 >= expectedDiscountAmount;

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.setAttribute('data-discount-valid', isMatch ? 'true' : 'false');
  }
}
